/**
 * What an order is being composed *against*.
 *
 * ADR-003 — an order carries a fulfillment context; it is not tied to a table.
 * The API proves it: `POST /orders { checkId, branchId, items }` is accepted with
 * `sessionId: null`, and the charge lands on that check. The dashboard only ever
 * offered the table route, so a floor with no tables — a kids area, a PS5
 * lounge, a garden with benches — could not be worked at all.
 *
 * One shape covers both, so `QuickOrderDialog` stays a single screen:
 *
 * - `table` — the classic route. There may be no session yet, in which case the
 *   dialog opens one first (§7), which is why `sessions.create` gates it.
 * - `check` — the bill itself is the context. Nothing is opened; the order is
 *   posted straight against `checkId`.
 *
 * The second used to be `party`, and the change is the backend's: a bill hung
 * off a party, so a party was the thing to post against and the guest inside it
 * carried the attribution. A bill hangs off a session now and charges carry no
 * person at all, so the check IS the context — which is also what a resource
 * session already holds (`ResourceSession.checkId`), pinned at start.
 */
import type { LiveTable } from './live-table';

export interface TableOrderTarget {
  kind: 'table';
  table: LiveTable;
  /** The live session, when the table already has one. */
  sessionId: string | null;
}

export interface CheckOrderTarget {
  kind: 'check';
  checkId: string;
  /** What this bill is called on screen — the dialog's title. */
  label: string;
  /**
   * Derived from the check when omitted, but the dialog needs it up front to
   * format money in the right currency, so callers pass what they know.
   */
  branchId: string | null;
  /** A line under the title: which child, which area this order is for. */
  context?: string | null;
}

export type OrderTarget = TableOrderTarget | CheckOrderTarget;

/** The branch whose currency and price list this order belongs to. */
export function branchOf(target: OrderTarget): string | null {
  return target.kind === 'table' ? target.table.branchId : target.branchId;
}

/**
 * The `POST /orders` body.
 *
 * The global `ValidationPipe` runs `forbidNonWhitelisted`, so a key
 * `CreateOrderDto` does not declare is a 400 — every field below is one it
 * declares, and an absent id is omitted rather than sent as null.
 *
 * Prices are never sent: the client shows what it read, the server prices what
 * it charges (§38), so a stale menu cannot undercharge.
 */
export function orderBodyFor(
  target: OrderTarget,
  options: {
    /** A table target resolves its session id first; a check target has none. */
    sessionId?: string | null;
    items: { itemId: string; quantity: number }[];
    submit: boolean;
  },
): Record<string, unknown> {
  const branchId = branchOf(target);
  return {
    ...(target.kind === 'table'
      ? { sessionId: options.sessionId ?? undefined }
      : { checkId: target.checkId }),
    ...(branchId ? { branchId } : {}),
    fulfillmentType: 'DINE_IN',
    items: options.items,
    // A DRAFT cannot go straight to the kitchen (§8.1); `submit` confirms and
    // fires in the same call, which is what "send" means here.
    submit: options.submit,
  };
}
